/**
 * Мінімальний рендер Markdown для довідника (ч.979 — переписаний).
 *
 * ЧОМУ СВІЙ, А НЕ БІБЛІОТЕКА: у проєкті немає md-рендера, а додавати
 * залежність (react-markdown ≈ 100 КБ) заради довідки не варто — бандл і
 * так 4.5 МБ (TD-BUNDLE-SENTINEL-ASLEEP).
 *
 * ЩО БУЛО НЕ ТАК у першій версії (знайшов власник: «текст криво
 * відформатований у html», перевірено дампом HTML):
 *   • абзац, перенесений у джерелі на кілька рядків, ставав КІЛЬКОМА
 *     `<p>` — текст сипався на шматки;
 *   • продовження пункту списку («   від нього залежить…») вивалювалось
 *     з `<li>` окремим абзацом з відступом-сміттям;
 *   • кожен пункт отримував ВЛАСНИЙ `<ul>` замість одного списку;
 *   • нумерований список рендерився як маркований.
 * Тепер рендер блоковий: спершу ділимо на блоки по порожньому рядку,
 * далі кожен блок розбираємо цілком.
 *
 * БЕЗПЕКА: текст екранується ДО розмітки, вставити HTML через статтю
 * неможливо. Посилання дозволені лише http(s) і внутрішні (/…).
 */

const esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** Жирний, курсив, код, посилання — вже на екранованому тексті. */
function inline(s: string): string {
  let out = esc(s);
  out = out.replace(/`([^`]+)`/g, '<code>$1</code>');
  out = out.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
  out = out.replace(/(^|[\s(])\*([^*\n]+)\*/g, '$1<em>$2</em>');
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (_m, text: string, href: string) => {
    if (!/^(https?:\/\/|\/)/i.test(href)) return text;
    const ext = href.startsWith('http');
    return `<a href="${href}"${ext ? ' target="_blank" rel="noopener noreferrer"' : ''}>${text}</a>`;
  });
  // [[внутрішнє-посилання]] з KB — показуємо як текст.
  out = out.replace(/\[\[([^\]]+)\]\]/g, '<em>$1</em>');
  return out;
}

const RE_HEADING = /^(#{1,6})\s+(.*)$/;
const RE_HR = /^\s*(?:---+|\*\*\*+)\s*$/;
const RE_LI = /^\s*(?:([-*+])|(\d+)[.)])\s+(.*)$/;
const RE_TABLE_SEP = /^\s*\|[\s:|-]+\|\s*$/;

/** Рядки блоку → один абзац: мʼякі переноси джерела НЕ рвуть текст. */
const para = (lines: string[]): string =>
  `<p>${inline(lines.join(' ').replace(/\s+/g, ' ').trim())}</p>`;

function renderList(block: string[]): string {
  // Тип списку — за першим пунктом; продовження рядків приєднуються
  // до поточного пункту, а не стають окремим абзацом.
  const first = RE_LI.exec(block[0] ?? '');
  const ordered = !!first?.[2];
  const items: string[][] = [];
  for (const line of block) {
    const m = RE_LI.exec(line);
    if (m) items.push([m[3] ?? '']);
    else if (items.length) items[items.length - 1]!.push(line.trim());
    else items.push([line.trim()]);
  }
  const body = items
    .map((parts) => `<li>${inline(parts.join(' ').replace(/\s+/g, ' ').trim())}</li>`)
    .join('');
  return ordered ? `<ol>${body}</ol>` : `<ul>${body}</ul>`;
}

function renderTable(block: string[]): string {
  const cells = (l: string) =>
    l.trim().replace(/^\||\|$/g, '').split('|').map((c) => c.trim());
  const head = cells(block[0] ?? '');
  const rows = block.slice(2).filter((l) => l.trim().startsWith('|')).map(cells);
  return '<table class="help-md__table"><thead><tr>'
    + head.map((c) => `<th>${inline(c)}</th>`).join('')
    + '</tr></thead><tbody>'
    + rows.map((r) => `<tr>${r.map((c) => `<td>${inline(c)}</td>`).join('')}</tr>`).join('')
    + '</tbody></table>';
}

/** ч.1336: розділи статті (заголовки «##») для змісту над довгою статтею. Ідентифікатори
 *  збігаються з тими, що `renderMarkdown` ставить заголовкам, — за порядком: `sec-1`, `sec-2`… */
export function articleSections(md: string): { id: string; title: string }[] {
  const out: { id: string; title: string }[] = [];
  let inCode = false;
  for (const line of md.replace(/\r\n/g, '\n').split('\n')) {
    if (line.trim().startsWith('```')) { inCode = !inCode; continue; }
    if (inCode) continue;
    const h = RE_HEADING.exec(line);
    if (h && h[1]!.length === 2) {
      out.push({ id: `sec-${out.length + 1}`, title: (h[2] ?? '').replace(/\*\*/g, '').trim() });
    }
  }
  return out;
}

export function renderMarkdown(md: string): string {
  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let i = 0;
  let section = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (!line.trim()) { i++; continue; }

    // Блок коду ``` — віддаємо як є.
    if (line.trim().startsWith('```')) {
      const buf: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').trim().startsWith('```')) {
        buf.push(lines[i] ?? ''); i++;
      }
      i++;
      out.push(`<pre><code>${esc(buf.join('\n'))}</code></pre>`);
      continue;
    }

    const h = RE_HEADING.exec(line);
    if (h) {
      // «#» у статті — її назва, вона вже показана вгорі → від h2 і нижче.
      const level = Math.min(6, Math.max(2, h[1]!.length));
      // ч.1336: якір розділу «##» — для змісту над довгою статтею (articleSections).
      const anchor = h[1]!.length === 2 ? ` id="sec-${++section}"` : '';
      out.push(`<h${level}${anchor}>${inline(h[2] ?? '')}</h${level}>`);
      i++; continue;
    }

    if (RE_HR.test(line)) { out.push('<hr/>'); i++; continue; }

    // Таблиця: заголовок + рядок-роздільник.
    if (line.trim().startsWith('|') && RE_TABLE_SEP.test(lines[i + 1] ?? '')) {
      const block: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('|')) {
        block.push(lines[i] ?? ''); i++;
      }
      out.push(renderTable(block));
      continue;
    }

    // Цитата: усі поспіль «>»-рядки — один блок.
    if (line.trim().startsWith('>')) {
      const block: string[] = [];
      while (i < lines.length && (lines[i] ?? '').trim().startsWith('>')) {
        block.push((lines[i] ?? '').replace(/^\s*>\s?/, '')); i++;
      }
      out.push(`<blockquote>${para(block)}</blockquote>`);
      continue;
    }

    // Список: до порожнього рядка або початку іншого блоку.
    if (RE_LI.test(line)) {
      const block: string[] = [];
      while (i < lines.length) {
        const cur = lines[i] ?? '';
        if (!cur.trim()) break;
        if (RE_HEADING.test(cur) || RE_HR.test(cur) || cur.trim().startsWith('|')
          || cur.trim().startsWith('```')) break;
        block.push(cur); i++;
      }
      out.push(renderList(block));
      continue;
    }

    // Звичайний абзац: до порожнього рядка або початку іншого блоку.
    const block: string[] = [];
    while (i < lines.length) {
      const cur = lines[i] ?? '';
      if (!cur.trim()) break;
      if (RE_HEADING.test(cur) || RE_HR.test(cur) || RE_LI.test(cur)
        || cur.trim().startsWith('|') || cur.trim().startsWith('>')
        || cur.trim().startsWith('```')) break;
      block.push(cur); i++;
    }
    out.push(para(block));
  }

  return out.join('\n');
}

export default renderMarkdown;
